import { CloseRounded } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import { Button, Dialog, DialogContent, Box, Typography, DialogActions, TextField, useMediaQuery } from '@mui/material'
import { blue } from '@mui/material/colors'
import React from 'react'
import { useGlobalSnackbarStore } from '../../../store/GlobalSnackBarStore'
import { useDialogOpened } from '../../../utils/hooks/useDialogOpened'
import { AdoptersClient } from '../../../interactors/clients/AdoptersClient'
import { AdoptionAttemptDto } from '../../../interactors/gen/backendClient'
import { useMutation } from 'react-query'
import { theme } from '../../theme'
import { addPrimaryFabSx, addSecondaryFabSx } from '../../common/addPrimaryCtaSx'

type Props = {
  adopterId: string
  adoptionAttempt: AdoptionAttemptDto
  type: 'certificate' | 'contract' | 'sterilization'
  setAdoptionAttempt: React.Dispatch<React.SetStateAction<AdoptionAttemptDto | undefined>>
  disabled?: boolean
  buttonText?: string
  sx?: Record<string, unknown>
  iconOnly?: boolean
  fullWidth?: boolean
}

export const RefuseFileModal: React.FC<Props> = ({
  adopterId,
  adoptionAttempt,
  type,
  setAdoptionAttempt,
  disabled = false,
  buttonText = 'Refuser le document',
  sx,
  iconOnly = false,
  fullWidth = false,
}) => {
  const { isDialogOpen, openDialog, closeDialog } = useDialogOpened()
  const globalSnackbar = useGlobalSnackbarStore()
  const [refusedReason, setRefusedReason] = React.useState<string>()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const mutation = useMutation(
    async (data: AdoptionAttemptDto) => {
      return await AdoptersClient.updateAdoptionAttempt(adopterId, data)
    },
    {
      onSuccess: (data) => {
        setAdoptionAttempt(data)
        closeDialog()
        globalSnackbar.triggerSuccessMessage(`Votre document a bien été ajouté !`)
      },
    }
  )

  const onSubmit = () => {
    if (refusedReason) {
      mutation.mutate({
        ...adoptionAttempt,
        [type]: {
          ...adoptionAttempt[type],
          refusedReason,
          signed: undefined,
          signedDate: undefined,
          docuSealSlug: undefined,
          file: undefined,
        },
        status: getNewStatusForType(type),
      })
    } else {
      globalSnackbar.triggerErrorMessage(`Vous devez ajouter une raison !`)
    }
  }
  return (
    <>
      {iconOnly ? (
        <Button
          variant="outlined"
          onClick={openDialog}
          disabled={disabled}
          sx={{
            minWidth: 0,
            width: 30,
            height: 30,
            borderRadius: 2,
            color: '#B35558',
            borderColor: 'rgba(179, 85, 88, 0.28)',
            backgroundColor: 'rgba(179, 85, 88, 0.04)',
            px: 0,
            '& .MuiSvgIcon-root': { fontSize: 16 },
            ...sx,
          }}
        >
          <CloseRounded />
        </Button>
      ) : (
        <Button
          variant="outlined"
          fullWidth={fullWidth}
          sx={{
            ...addSecondaryFabSx,
            color: '#B35558',
            borderColor: 'rgba(179, 85, 88, 0.28)',
            backgroundColor: 'rgba(179, 85, 88, 0.04)',
            fontSize: { xs: '14px', sm: '15px' },
            ...sx,
          }}
          onClick={openDialog}
          disabled={disabled}
        >
          {buttonText}
        </Button>
      )}
      <Dialog
        open={isDialogOpen}
        onClose={() => {
          closeDialog()
        }}
        PaperProps={{
          style: { borderRadius: 15, padding: 15 },
        }}
      >
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography
              variant="h4"
              component="h2"
              sx={{ fontSize: { xs: '18px', sm: '20px' }, color: blue, mb: { xs: 3, sm: 4 } }}
            >
              {`Voulez-vous vraiment refuser le document ?`}
            </Typography>
            <TextField label="Raison du refus" multiline rows={4} onChange={(e) => setRefusedReason(e.target.value)} />
          </Box>
        </DialogContent>
        <DialogActions>
          <>
            <Button
              data-testid="cancel"
              onClick={() => {
                closeDialog()
              }}
              variant={isMobile ? 'outlined' : 'text'}
              sx={{
                textTransform: 'none',
                fontSize: { xs: '14px', sm: '16px' },
                width: { xs: '40%', sm: 'auto' },
              }}
            >
              Annuler
            </Button>
            <LoadingButton
              data-testid="apply-changes"
              variant="contained"
              type="submit"
              sx={{
                ...addPrimaryFabSx,
                fontSize: { xs: '14px', sm: '15px' },
                width: { xs: '60%', sm: 'auto' },
                px: { xs: '5px', sm: 2.25 },
              }}
              loading={mutation.isLoading}
              onClick={onSubmit}
            >
              Refuser le document
            </LoadingButton>{' '}
          </>
        </DialogActions>
      </Dialog>
    </>
  )
}

function getNewStatusForType(type: 'certificate' | 'contract' | 'sterilization') {
  switch (type) {
    case 'certificate':
      return 'awaiting-signed-certificate'
    case 'contract':
      return 'awaiting-signed-contract'
    case 'sterilization':
      return 'awaiting-signed-contract'
  }
}
